/**
 * User Service
 *
 * Manages the current user's profile and authentication state.
 * When running in embedded mode, integrates with the host environment
 * to obtain user context and handle impersonation.
 */

import { Injectable, inject, OnDestroy } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { HOST_BRIDGE } from './host-bridge/host-bridge.factory';
import { HostBridge, UserContext, IncomingMessageTypes } from './host-bridge/host-bridge.interface';

/**
 * User profile information for impersonation context
 */
export interface ImpersonatorInfo {
  sys_id: string;
  user_name: string;
  display_name: string;
  title?: string;
  avatar_url?: string;
}

/**
 * Extended user profile interface with ServiceNow integration fields
 */
export interface UserProfile {
  // Core fields (backward compatible)
  name: string;
  role: string;
  avatar: string;
  title?: string;

  // ServiceNow integration fields
  sn_sys_id?: string;
  sn_user_name?: string;
  email?: string;
  roles?: string[];

  // Impersonation fields
  isImpersonating?: boolean;
  impersonatedBy?: ImpersonatorInfo;
}

/**
 * Default avatar paths for known personas
 */
const AVATAR_MAP: Record<string, string> = {
  'demo.claire': 'user-avatars/personas/claire.png',
  'demo.jason': 'user-avatars/personas/jason-inventory-manager.png',
  'demo.kelly': 'user-avatars/personas/kelly.png',
  'demo.john': 'user-avatars/personas/john.png',
};

const DEFAULT_AVATAR = 'user-avatars/default.png';

@Injectable({ providedIn: 'root' })
export class UserService implements OnDestroy {
  private readonly hostBridge = inject(HOST_BRIDGE);
  private readonly userSubject = new BehaviorSubject<UserProfile | null>(null);
  private readonly userContextChangedHandler: (context: UserContext) => void;

  /**
   * Observable of the current user profile
   */
  readonly user$ = this.userSubject.asObservable();

  constructor() {
    // Set up listener for user context changes from host (e.g., impersonation)
    this.userContextChangedHandler = (context: UserContext) => {
      console.log('[UserService] User context changed from host:', context);
      const profile = this.mapUserContextToProfile(context);
      this.setUser(profile);
    };

    this.hostBridge.onMessage<UserContext>(
      IncomingMessageTypes.USER_CONTEXT_CHANGED,
      this.userContextChangedHandler
    );
  }

  ngOnDestroy(): void {
    this.hostBridge.offMessage(
      IncomingMessageTypes.USER_CONTEXT_CHANGED,
      this.userContextChangedHandler
    );
  }

  /**
   * Load user context from the host environment.
   * Should be called during app initialization when in embedded mode.
   *
   * @throws Error if unable to load user context from host
   */
  async loadUserFromHost(): Promise<void> {
    try {
      console.log('[UserService] Loading user context from host...');
      const userContext = await this.hostBridge.getUserContext();
      const userProfile = this.mapUserContextToProfile(userContext);
      this.setUser(userProfile);
      console.log('[UserService] User context loaded successfully:', userProfile.name);
    } catch (error) {
      console.error('[UserService] Failed to load user context from host:', error);
      throw error;
    }
  }

  /**
   * Set the current user profile
   */
  setUser(user: UserProfile): void {
    this.userSubject.next(user);

    // Notify host if embedded
    if (this.hostBridge.isEmbedded()) {
      this.hostBridge.sendMessage('USER_LOADED', {
        name: user.name,
        role: user.role,
        email: user.email,
        sn_sys_id: user.sn_sys_id,
      });
    }
  }

  /**
   * Get the current user profile (synchronous)
   */
  getUser(): UserProfile | null {
    return this.userSubject.value;
  }

  /**
   * Check if the current user session involves impersonation
   */
  isImpersonating(): boolean {
    return this.userSubject.value?.isImpersonating ?? false;
  }

  /**
   * Get information about who is impersonating (if applicable)
   */
  getImpersonator(): ImpersonatorInfo | undefined {
    return this.userSubject.value?.impersonatedBy;
  }

  /**
   * Check if the app is running in embedded mode
   */
  isEmbedded(): boolean {
    return this.hostBridge.isEmbedded();
  }

  /**
   * Clear the current user (logout)
   */
  clearUser(): void {
    this.userSubject.next(null);
  }

  /**
   * Map ServiceNow UserContext to app's UserProfile format
   */
  private mapUserContextToProfile(context: UserContext): UserProfile {
    return {
      // Core fields
      name: context.display_name,
      role: this.mapRolesToPrimaryRole(context.roles),
      avatar: this.normalizeAvatarUrl(context.avatar_url) || this.getAvatarForUser(context.user_name),
      title: context.title,

      // ServiceNow fields
      sn_sys_id: context.sys_id,
      sn_user_name: context.user_name,
      email: context.email,
      roles: context.roles,

      // Impersonation fields
      isImpersonating: context.isImpersonating,
      impersonatedBy: context.impersonatedBy
        ? {
            sys_id: context.impersonatedBy.sys_id,
            user_name: context.impersonatedBy.user_name,
            display_name: context.impersonatedBy.display_name,
            title: context.impersonatedBy.title,
            avatar_url: context.impersonatedBy.avatar_url,
          }
        : undefined,
    };
  }

  /**
   * Map ServiceNow roles to a primary app role for display
   */
  private mapRolesToPrimaryRole(roles: string[]): string {
    // Priority order: admin > manager > user > viewer
    if (roles.some((r) => r.toLowerCase().includes('admin'))) {
      return 'Admin';
    }
    if (roles.some((r) => r.toLowerCase().includes('manager'))) {
      return 'Manager';
    }
    if (roles.some((r) => r.toLowerCase().includes('user'))) {
      return 'User';
    }
    return 'Viewer';
  }

  /**
   * Get avatar path for a user based on their username
   */
  private getAvatarForUser(userName: string): string {
    return AVATAR_MAP[userName] || DEFAULT_AVATAR;
  }

  /**
   * Normalize avatar URL to fix double slashes.
   * Handles data URLs (base64) by returning them as-is since they can be used directly in img src.
   * @param url The avatar URL (can be a data URL, relative path, or absolute URL)
   */
  private normalizeAvatarUrl(url: string | undefined): string | undefined {
    if (!url) {
      return undefined;
    }

    // Data URLs (base64) can be used directly - no processing needed
    if (url.startsWith('data:')) {
      return url;
    }

    let normalizedUrl = url;

    // Fix double slashes in the path (but preserve the // after protocol)
    normalizedUrl = normalizedUrl.replace(/([^:]\/)\/+/g, '$1');

    return normalizedUrl;
  }
}
